/**
 * Custom Hook for Cart Operations
 * Provides easy access to cart service with Redux integration
 */

import { useDispatch, useSelector } from "react-redux";
import { POST_SELECTED_CHEF, POST_TO_CART } from "../store/action";
import CartService from "../utils/cartService";

export const useCart = () => {
  const cart = useSelector((state) => state.cart);
  const selectedChef = useSelector((state) => state.selectedChef);
  const dispatch = useDispatch();

  /**
   * Update cart in Redux store
   * @param {Array} newCart - New cart state
   * @param {Object} newSelectedChef - New selected chef (optional)
   */
  const updateCart = (newCart, newSelectedChef = selectedChef) => {
    dispatch({ type: POST_TO_CART, data: newCart });
    dispatch({ type: POST_SELECTED_CHEF, data: newSelectedChef });
  };

  /**
   * Add item to cart
   * @param {Object} item - Item to add
   * @returns {Object} - Result with validation info
   */
  const addItem = (item) => {
    const result = CartService.addItem(cart, item, selectedChef);
    updateCart(result.cart);
    return result;
  };

  /**
   * Remove item from cart
   * @param {string} itemId - ID of item to remove
   * @returns {Object} - Result with validation info
   */
  const removeItem = (itemId) => {
    const result = CartService.removeItem(cart, itemId);
    updateCart(result.cart);
    return result;
  };

  /**
   * Update item quantity
   * @param {string} itemId - ID of item to update
   * @param {number} quantity - New quantity
   * @returns {Object} - Result with validation info
   */
  const updateQuantity = (itemId, quantity) => {
    const result = CartService.updateQuantity(cart, itemId, quantity);
    updateCart(result.cart);
    return result;
  };

  /**
   * Decrease item quantity by 1
   * @param {string} itemId - ID of item to decrease
   * @returns {Object} - Result with validation info
   */
  const decreaseQuantity = (itemId) => {
    const result = CartService.decreaseQuantity(cart, itemId);
    updateCart(result.cart);
    return result;
  };

  /**
   * Increase item quantity by 1
   * @param {string} itemId - ID of item to increase
   * @returns {Object} - Result with validation info
   */
  const increaseQuantity = (itemId) => {
    const result = CartService.increaseQuantity(cart, itemId);
    updateCart(result.cart);
    return result;
  };

  /**
   * Toggle item in cart (add if not present, remove if present)
   * @param {Object} item - Item to toggle
   * @returns {Object} - Result with validation info
   */
  const toggleItem = (item) => {
    const isInCart = CartService.isItemInCart(cart, item._id);
    if (isInCart) {
      return removeItem(item._id);
    } else {
      return addItem(item);
    }
  };

  /**
   * Clear entire cart
   */
  const clearCart = () => {
    updateCart([]);
  };

  /**
   * Get cart summary
   * @returns {Object} - Cart summary
   */
  const getSummary = () => {
    return CartService.getCartSummary(cart);
  };

  /**
   * Get cart statistics
   * @returns {Object} - Cart statistics
   */
  const getStats = () => {
    return CartService.getCartStats(cart);
  };

  /**
   * Check if item is in cart
   * @param {string} itemId - Item ID to check
   * @returns {boolean} - Whether item is in cart
   */
  const isItemInCart = (itemId) => {
    return CartService.isItemInCart(cart, itemId);
  };

  /**
   * Get item from cart
   * @param {string} itemId - Item ID
   * @returns {Object|null} - Item or null if not found
   */
  const getItem = (itemId) => {
    return CartService.getItemFromCart(cart, itemId);
  };

  /**
   * Validate cart state
   * @returns {Object} - Validation result
   */
  const validate = () => {
    return CartService.validateCart(cart, selectedChef);
  };

  /**
   * Check if cart is empty
   * @returns {boolean} - Whether cart is empty
   */
  const isEmpty = () => {
    return cart.length === 0;
  };

  /**
   * Check if cart has items from different chef
   * @returns {boolean} - Whether cart has chef conflicts
   */
  const hasChefConflict = () => {
    const validation = CartService.validateChefConsistency(cart, selectedChef);
    return validation.shouldShowWarning;
  };

  return {
    // Cart state
    cart,
    selectedChef,

    // Cart operations
    addItem,
    removeItem,
    updateQuantity,
    decreaseQuantity,
    increaseQuantity,
    toggleItem,
    clearCart,

    // Cart queries
    getSummary,
    getStats,
    isItemInCart,
    getItem,
    validate,
    isEmpty,
    hasChefConflict,

    // Direct access to service (for advanced use cases)
    service: CartService
  };
};

export default useCart;
